"use client";

import { useState } from "react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

type Tab = {
  id: string;
  label: string;
  editable: ReturnType<typeof storyblokEditable>;
  content: React.ReactNode;
};

export default function TabbedModuleView({ blok, tabs }: { blok: SbBlokData; tabs: Tab[] }) {
  const [active, setActive] = useState(0);

  if (tabs.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)}>
      <div className="px-5 md:px-10 lg:px-20">
        <div className="mx-auto w-full max-w-7xl pt-10 lg:pt-20">
          <div
            role="tablist"
            className="border-grey-01 flex scrollbar-none gap-2 overflow-x-auto border-b pb-8 md:flex-wrap md:overflow-visible"
          >
            {tabs.map((tab, i) => {
              const isActive = i === active;
              return (
                <button
                  key={tab.id}
                  {...tab.editable}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={(e) => {
                    setActive(i);
                    e.currentTarget.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                  className={`inline-flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-full px-6 font-medium transition-colors ${
                    isActive
                      ? "bg-teal-07 text-white"
                      : "border-grey-01 text-grey-06 hover:border-teal-04 hover:text-teal-07 border bg-white"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {tabs.map((tab, i) => (
        <div key={`${tab.id}-${i === active}`} role="tabpanel" hidden={i !== active}>
          {tab.content}
        </div>
      ))}
    </section>
  );
}
